// react
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';

// data stubs
import theme from '../../data/theme';
import { resetAuth } from '../../store/auth/authActions';
import {
    IconUser,
    IconUserCircle,
    IconMail,
    IconPhone,
    IconCrown,
    IconIdCard,
    IconEdit,
    IconShieldLock,
    IconKey,
    IconCamera,
    IconVerifiedCheck,
} from './ProfileIcons';

const getInitialUserData = () => {
    try {
        const cached = localStorage.getItem('user_profile_data');
        return cached ? JSON.parse(cached) : null;
    } catch {
        return null;
    }
};

const getInitialAddressData = () => {
    try {
        const cached = localStorage.getItem('user_address_data');
        return cached ? JSON.parse(cached) : null;
    } catch {
        return null;
    }
};

export default function AccountPageDashboard() {
    const initialUser = getInitialUserData();
    const [address, setAddress] = useState(getInitialAddressData);
    const [userData, setUserData] = useState(initialUser);
    const [loadingData, setLoadingData] = useState(!initialUser);
    const [avatarLoaded, setAvatarLoaded] = useState(false);
    const [avatarError, setAvatarError] = useState(false);

    const dispatch = useDispatch();
    const history = useHistory();
    const { token } = useSelector((state) => state.auth);

    useEffect(() => {
        let isMounted = true;

        const fetchUserData = async () => {
            try {
                const userRes = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/auth/info`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (isMounted) {
                    const uData = userRes.data?.response;
                    setUserData(uData);
                    if (uData) {
                        localStorage.setItem('user_profile_data', JSON.stringify(uData));
                        if (uData.user_code) localStorage.setItem('user_code', uData.user_code);
                        if (uData.id) localStorage.setItem('user_id', uData.id);
                    }

                    // Fetch address if user has address_id
                    if (uData?.address_id) {
                        try {
                            const addrRes = await axios({
                                method: 'GET',
                                url: `${process.env.REACT_APP_HOST_API}/address/get/${uData.address_id}`,
                                headers: {
                                    Authorization: `Bearer ${token}`,
                                },
                            });
                            if (isMounted && addrRes.data?.response) {
                                setAddress(addrRes.data.response);
                                localStorage.setItem('user_address_data', JSON.stringify(addrRes.data.response));
                            }
                        } catch (err) {
                            // Try fallback to address list
                            fetchFallbackAddress();
                        }
                    } else {
                        // Check if user has addresses in address list
                        fetchFallbackAddress();
                    }
                }
            } catch (error) {
                if (isMounted) {
                    dispatch(resetAuth());
                }
            } finally {
                if (isMounted) {
                    setLoadingData(false);
                }
            }
        };

        const fetchFallbackAddress = async () => {
            try {
                const listRes = await axios({
                    method: 'GET',
                    url: `${process.env.REACT_APP_HOST_API}/address/list`,
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const list = listRes.data?.response;
                if (isMounted && Array.isArray(list) && list.length > 0) {
                    setAddress(list[0]);
                    localStorage.setItem('user_address_data', JSON.stringify(list[0]));
                }
            } catch (err) {
                // address list empty or failed
            }
        };

        if (token) {
            fetchUserData();
        }

        return () => {
            isMounted = false;
        };
    }, [token, dispatch]);

    const rawImage = userData?.user_image;
    const isPlaceholderImage = !rawImage || 
        rawImage.includes('avatar-3.jpg') || 
        rawImage.includes('avatar-1.jpg') || 
        rawImage.includes('avatar-2.jpg') || 
        rawImage.includes('avatar-4.jpg') ||
        rawImage.includes('90x90');
    const hasValidUserImage = !isPlaceholderImage && !avatarError;

    useEffect(() => {
        if (rawImage && !isPlaceholderImage) {
            setAvatarLoaded(false);
            setAvatarError(false);
        }
    }, [rawImage, isPlaceholderImage]);

    const memberLevel = (userData?.member_level || 'REGULER').toUpperCase();
    const tierClass = memberLevel === 'GOLD' ? 'tier--gold' : memberLevel === 'SILVER' ? 'tier--silver' : '';

    const displayPhone = (address?.phone && address.phone !== '0')
        ? address.phone
        : (userData?.phone && userData.phone !== '0')
            ? userData.phone
            : null;

    const handleLogout = () => {
        dispatch(resetAuth());
        history.push('/account/login');
    };

    const menuGroups = [
        {
            title: 'Pengaturan Akun',
            items: [
                { icon: 'fa-shopping-bag', title: 'Pesanan Saya', desc: 'Lihat riwayat dan status pesanan', to: '/account/orders' },
                { icon: 'fa-map-marker-alt', title: 'Daftar Alamat', desc: 'Atur alamat pengiriman belanjaan', to: 'addresses' },
                { icon: 'fa-lock', title: 'Keamanan Akun', desc: 'Ganti kata sandi akun', to: 'password' },
            ],
        },
        {
            title: 'Bantuan & Informasi',
            items: [
                { icon: 'fa-question-circle', title: 'Pertanyaan Umum', desc: 'Jawaban seputar belanja di Hokky', to: '/site/faq' },
            ],
        },
    ];

    const mobileView = (
        <div className="account-mobile d-md-none">
            <Helmet>
                <title>{`Profil Saya | ${theme.name}`}</title>
            </Helmet>

            <div className="account-mobile__head">
                <div className="account-mobile__avatar">
                    {hasValidUserImage ? (
                        <img src={rawImage} alt={userData?.name || 'Profil'} onError={() => setAvatarError(true)} />
                    ) : (
                        <span>{userData?.name ? userData.name.trim().charAt(0).toUpperCase() : 'H'}</span>
                    )}
                </div>
                <div className="account-mobile__who">
                    {loadingData ? (
                        <React.Fragment>
                            <div className="skeleton-bone" style={{ width: '120px', height: '20px', borderRadius: '6px' }} />
                            <div className="skeleton-bone" style={{ width: '170px', height: '14px', borderRadius: '6px', marginTop: '8px' }} />
                        </React.Fragment>
                    ) : (
                        <React.Fragment>
                            <h2 className="account-mobile__name">{userData?.name || 'Pengguna Hokky'}</h2>
                            <div className="account-mobile__email">{userData?.email || '-'}</div>
                            <span className={`account-mobile__tier ${tierClass}`}>
                                <i className="fas fa-crown" /> {memberLevel} Member
                            </span>
                        </React.Fragment>
                    )}
                </div>
                <Link to="profile" className="account-mobile__edit" aria-label="Edit profil">
                    <i className="fas fa-pen" />
                </Link>
            </div>

            <div className="account-mobile__stats">
                <div className="account-mobile__stat">
                    <i className="fas fa-id-card" />
                    <strong>#{userData?.user_code || userData?.id || '-'}</strong>
                    <span>ID Member</span>
                </div>
                <div className="account-mobile__stat">
                    <i className="fas fa-phone-alt" />
                    <strong>{displayPhone || 'Belum diisi'}</strong>
                    <span>No. Handphone</span>
                </div>
                <div className="account-mobile__stat">
                    <i className="fas fa-crown" />
                    <strong>{memberLevel}</strong>
                    <span>Tier Member</span>
                </div>
            </div>

            {menuGroups.map((group) => (
                <section key={group.title} className="account-mobile__group">
                    <h3 className="account-mobile__group-title">{group.title}</h3>
                    <ul className="account-mobile__list">
                        {group.items.map((item) => (
                            <li key={item.title}>
                                <Link to={item.to} className="account-mobile__row">
                                    <span className="account-mobile__row-icon"><i className={`fas ${item.icon}`} /></span>
                                    <span className="account-mobile__row-text">
                                        <strong>{item.title}</strong>
                                        <small>{item.desc}</small>
                                    </span>
                                    <i className="fas fa-chevron-right account-mobile__row-arrow" />
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            ))}

            <section className="account-mobile__group">
                <ul className="account-mobile__list">
                    <li>
                        <button type="button" className="account-mobile__row account-mobile__row--danger" onClick={handleLogout}>
                            <span className="account-mobile__row-icon"><i className="fas fa-sign-out-alt" /></span>
                            <span className="account-mobile__row-text">
                                <strong>Keluar Akun</strong>
                                <small>Keluar dari akun di perangkat ini</small>
                            </span>
                        </button>
                    </li>
                </ul>
            </section>
        </div>
    );

    if (loadingData) {
        return (
            <React.Fragment>
            {mobileView}
            <div className="profile-dashboard-wrapper profile-skeleton-wrapper d-none d-md-flex">
                <Helmet>
                    <title>{`Profil Saya | ${theme.name}`}</title>
                </Helmet>

                {/* 1. Hero Card Skeleton */}
                <div className="profile-hero profile-hero--skeleton">
                    <div className="profile-hero__main">
                        <div className="profile-hero__avatar-box">
                            <div className="profile-hero__avatar-loader">
                                <i className="fas fa-spinner fa-spin profile-hero__avatar-spinner" />
                            </div>
                            <span className="profile-hero__badge-online" title="Akun Aktif" />
                        </div>

                        <div className="profile-hero__content" style={{ gap: '10px' }}>
                            <div className="skeleton-bone" style={{ width: '180px', height: '26px', borderRadius: '6px' }} />
                            <div className="skeleton-bone" style={{ width: '220px', height: '16px', borderRadius: '4px' }} />
                            <div className="skeleton-bone" style={{ width: '130px', height: '20px', borderRadius: '6px' }} />
                        </div>
                    </div>

                    <div className="profile-hero__actions">
                        <div className="skeleton-bone" style={{ width: '115px', height: '40px', borderRadius: '10px' }} />
                        <div className="skeleton-bone" style={{ width: '115px', height: '40px', borderRadius: '10px' }} />
                    </div>
                </div>

                {/* 2. Detail Grid Skeleton */}
                <div className="profile-detail-grid">
                    <div className="profile-box profile-box--skeleton">
                        <div className="profile-box__header">
                            <div className="profile-box__title-group">
                                <div className="skeleton-bone" style={{ width: '38px', height: '38px', borderRadius: '10px' }} />
                                <div className="skeleton-bone" style={{ width: '150px', height: '20px', borderRadius: '4px' }} />
                            </div>
                            <div className="skeleton-bone" style={{ width: '60px', height: '28px', borderRadius: '6px' }} />
                        </div>

                        <div className="profile-box__body">
                            <div className="profile-info-list">
                                {[
                                    { labelW: '90px', valW: '160px' },
                                    { labelW: '80px', valW: '220px' },
                                    { labelW: '95px', valW: '140px' },
                                    { labelW: '85px', valW: '110px' },
                                    { labelW: '80px', valW: '130px' },
                                ].map((item, idx) => (
                                    <div key={idx} className="profile-info-item">
                                        <div className="profile-info-item__label" style={{ gap: '10px' }}>
                                            <div className="skeleton-bone" style={{ width: '32px', height: '32px', borderRadius: '8px', flexShrink: 0 }} />
                                            <div className="skeleton-bone" style={{ width: item.labelW, height: '14px', borderRadius: '4px' }} />
                                        </div>
                                        <div className="skeleton-bone" style={{ width: item.valW, height: '16px', borderRadius: '4px' }} />
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="profile-box__footer">
                            <div className="skeleton-bone" style={{ width: '160px', height: '36px', borderRadius: '8px' }} />
                            <div className="skeleton-bone" style={{ width: '120px', height: '36px', borderRadius: '8px' }} />
                        </div>
                    </div>
                </div>
            </div>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
        {mobileView}
        <div className="profile-dashboard-wrapper d-none d-md-flex">
            <Helmet>
                <title>{`Profil Saya | ${theme.name}`}</title>
            </Helmet>

            {/* 1. Hero Card: User Profile Header */}
            <div className="profile-hero">
                <div className="profile-hero__main">
                    <div className="profile-hero__avatar-box">
                        {hasValidUserImage ? (
                            <>
                                {!avatarLoaded && (
                                    <div className="profile-hero__avatar-loader" title="Memuat foto profil">
                                        <i className="fas fa-spinner fa-spin profile-hero__avatar-spinner" />
                                    </div>
                                )}
                                <img
                                    src={rawImage}
                                    alt={userData?.name || 'User Profile'}
                                    className={`profile-hero__avatar ${avatarLoaded ? 'profile-hero__avatar--loaded' : 'profile-hero__avatar--loading'}`}
                                    ref={(img) => {
                                        if (img && img.complete && img.naturalWidth > 0 && !avatarLoaded) {
                                            setAvatarLoaded(true);
                                        }
                                    }}
                                    onLoad={() => setAvatarLoaded(true)}
                                    onError={() => {
                                        setAvatarLoaded(true);
                                        setAvatarError(true);
                                    }}
                                />
                            </>
                        ) : (
                            <div className="profile-hero__avatar profile-hero__avatar-placeholder">
                                {userData?.name ? userData.name.trim().charAt(0).toUpperCase() : 'H'}
                            </div>
                        )}
                        <span className="profile-hero__badge-online" title="Akun Aktif" />
                    </div>

                    <div className="profile-hero__content">
                        <div className="profile-hero__meta-top">
                            <h2 className="profile-hero__name">
                                {userData?.name || 'Pengguna Hokky'}
                                <span className="verified-wrapper" title="Akun Terverifikasi">
                                    <IconVerifiedCheck size={20} />
                                </span>
                            </h2>
                        </div>

                        <div className="profile-hero__email-row">
                            <IconMail size={15} color="#FFBA08" />
                            <span>{userData?.email || 'email@hokkybangunan.co.id'}</span>
                        </div>

                        {(userData?.user_code || userData?.id) && (
                            <div className="profile-hero__id-chip">
                                <IconIdCard size={13} color="rgba(255, 255, 255, 0.7)" />
                                <span>ID Member: #{userData.user_code || userData.id}</span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="profile-hero__actions">
                    <Link to="profile" className="btn-profile-primary">
                        <IconEdit size={16} />
                        <span>Edit Profil</span>
                    </Link>
                    <Link to="password" className="btn-profile-secondary">
                        <IconShieldLock size={16} />
                        <span>Password</span>
                    </Link>
                </div>
            </div>

            {/* 2. Detail Grid: Personal Information & Primary Shipping Address */}
            <div className="profile-detail-grid">
                {/* Left Card: Informasi Pribadi */}
                <div className="profile-box">
                    <div className="profile-box__header">
                        <div className="profile-box__title-group">
                            <div className="profile-box__header-icon icon--blue">
                                <IconUserCircle size={20} />
                            </div>
                            <h3 className="profile-box__title">Informasi Pribadi</h3>
                        </div>
                        <Link to="profile" className="profile-box__header-action">
                            <IconEdit size={14} />
                            <span>Ubah</span>
                        </Link>
                    </div>

                    <div className="profile-box__body">
                        <div className="profile-info-list">
                            <div className="profile-info-item">
                                <div className="profile-info-item__label">
                                    <div className="profile-info-item__icon">
                                        <IconUser size={16} />
                                    </div>
                                    <span>Nama Lengkap</span>
                                </div>
                                <span className="profile-info-item__val">
                                    {userData?.name || '-'}
                                </span>
                            </div>

                            <div className="profile-info-item">
                                <div className="profile-info-item__label">
                                    <div className="profile-info-item__icon">
                                        <IconMail size={16} />
                                    </div>
                                    <span>Alamat Email</span>
                                </div>
                                <span className="profile-info-item__val profile-info-item__email">
                                    {userData?.email || '-'}
                                </span>
                            </div>

                            <div className="profile-info-item">
                                <div className="profile-info-item__label">
                                    <div className="profile-info-item__icon">
                                        <IconPhone size={16} />
                                    </div>
                                    <span>No. Handphone</span>
                                </div>
                                <span className="profile-info-item__val">
                                    {displayPhone ? (
                                        displayPhone
                                    ) : (
                                        <span className="profile-val-unfilled">Belum ditambahkan</span>
                                    )}
                                </span>
                            </div>

                            <div className="profile-info-item">
                                <div className="profile-info-item__label">
                                    <div className="profile-info-item__icon">
                                        <IconCrown size={16} />
                                    </div>
                                    <span>Tier Member</span>
                                </div>
                                <span className="profile-info-item__val">
                                    <span className={`profile-tier-pill ${tierClass || 'tier--reguler'}`}>
                                        <IconCrown size={12} />
                                        <span>{memberLevel} Member</span>
                                    </span>
                                </span>
                            </div>

                            <div className="profile-info-item">
                                <div className="profile-info-item__label">
                                    <div className="profile-info-item__icon">
                                        <IconIdCard size={16} />
                                    </div>
                                    <span>ID Pengguna</span>
                                </div>
                                <span className="profile-info-item__val">
                                    <span className="profile-id-code">
                                        #{userData?.user_code || userData?.id || '-'}
                                    </span>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="profile-box__footer">
                        <Link to="password" className="profile-footer-btn">
                            <IconKey size={15} />
                            <span>Ganti Password Akun</span>
                        </Link>
                        <Link to="profile" className="profile-footer-btn">
                            <IconCamera size={15} />
                            <span>Ganti Foto</span>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
        </React.Fragment>
    );
}
